import { motion } from "framer-motion";
import { Shield, Lock, Eye, FileText, ChevronLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import { useEffect } from "react";

const PrivacyPolicy = () => {
  // Ensure the user starts at the top of the page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: <Eye className="w-6 h-6 text-primary" />,
      title: "Information Collection",
      content: "We collect information you provide directly to us through our quote request forms, including your name, email address, phone number, and project requirements. This helps us provide accurate structural solutions for your specific needs."
    },
    {
      icon: <Lock className="w-6 h-6 text-primary" />,
      title: "Data Security",
      content: "We implement industry-standard security measures to protect your personal information. Your data is stored securely and is only accessible by authorized personnel involved in processing your request."
    },
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: "Information Sharing",
      content: "BA Tents does not sell or lease your personal data to third parties. We only share information with partners essential to fulfilling your project requirements or when required by UAE law."
    },
    {
      icon: <FileText className="w-6 h-6 text-primary" />,
      title: "Your Rights",
      content: "You have the right to request access to the personal information we hold about you or to ask for corrections. For any data-related inquiries, please contact our administrative office in Dubai."
    }
  ];

  return (
    <div className="bg-background min-h-screen font-sans text-foreground overflow-x-hidden">
      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group text-sm font-medium text-muted-foreground hover:text-white transition-colors">
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          <div className="text-sm font-bold tracking-tighter uppercase">BA Tents</div>
        </div>
      </nav>

      {/* Hero Header */}
      <section className="pt-40 pb-20 px-6 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full bg-primary/5 blur-[120px] rounded-full" />
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-primary text-[10px] uppercase tracking-[0.4em] font-bold mb-4 block">Compliance & Legal</span>
            <h1 className="text-5xl md:text-7xl font-light tracking-tight mb-6">Privacy Policy</h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-light">
              Your trust is our most valuable asset. We are committed to protecting your privacy and being transparent about how we handle your data.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-32 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated Badge */}
          <div className="flex justify-center mb-16">
            <div className="bg-white/5 border border-white/10 px-4 py-1.5 rounded-full text-[11px] text-muted-foreground uppercase tracking-widest">
              Last Updated: April 2026
            </div>
          </div>

          {/* Policy Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sections.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative p-8 rounded-3xl bg-white/5 border border-white/10 hover:border-primary/30 transition-all duration-500"
              >
                <div className="mb-6 p-3 bg-primary/10 w-fit rounded-2xl group-hover:scale-110 transition-transform duration-500">
                  {item.icon}
                </div>
                <h3 className="text-xl font-medium mb-4">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed font-light">
                  {item.content}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Detailed Text Area */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-20 p-8 md:p-12 rounded-[2.5rem] bg-[#0D0D0D] border border-white/5"
          >
            <h2 className="text-3xl font-light mb-8">Detailed Terms</h2>
            <div className="space-y-8 text-muted-foreground font-light leading-relaxed">
              <p>
                By using the BA Tents website and services, you consent to the data practices described in this policy. We use "cookies" to enhance your browsing experience and analyze site traffic. You can choose to disable cookies through your browser settings, though some site features may become unavailable.
              </p>
              <div className="h-px bg-white/10 w-full" />
              <div>
                <h4 className="text-white font-medium mb-2">Contact for Data Protection</h4>
                <p className="text-sm">
                  If you have questions regarding this policy or our treatment of your personal data, please reach out to our legal department at:
                </p>
                <a 
                  href="mailto:legal@baitnokhada.com" 
                  className="inline-flex items-center gap-2 mt-4 text-primary hover:text-primary/80 transition-colors"
                >
                  info@batents.ae <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;